import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Col, FormGroup, Label, Loader, Row, Select,
} from 'idsk-react-components';
import { OrganizacnaSkupinaEnum } from 'ciselniky-frontend-ts-axios';
import { OrganizaciaInput } from 'slovlex-idsk-react-components';
import routes from '../../../routing/routes';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import { useClient } from '../../../context/ClientStatusContext';
import useFieldErrorMessages from '../../useFieldErrorMessages';
import { changeGestor } from '../../../client/pracovnyKrok/urcenieGestoraClient';
import useOrganizacie from '../../../hooks/useOrganizacie';
import UrcenieGestoraButtons from './component/UrcenieGestoraButtons';
import LegislativnyMaterialDetail
  from '../../../model/legislativnyMaterial/LegislativnyMaterialDetail';
import './urcenieGestora.scss';
import Organizacia from '../../../model/ciselniky/Organizacia';

interface Props {
  materialDetail: LegislativnyMaterialDetail;
  isAdmin?: boolean;
}

const UrcenieGestora = ({ materialDetail, isAdmin }: Props): ReactElement => {
  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();
  const {
    organizacie: organizaciaList,
    organizacieStatus: organizaciaListStatus,
  } = useOrganizacie(OrganizacnaSkupinaEnum.Gestor, true);
  const {
    type,
    year,
    index,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number, }>();

  const [gestor, setGestor] = useState<string>();
  const [spolugestori, setSpolugestori] = useState<Array<Organizacia>>();

  useEffect(() => {
    if (organizaciaList && organizaciaList.length !== 0 && materialDetail.gestor
        && (!organizaciaList.some(org => org?.uuid === materialDetail?.gestor?.uuid))) {
      organizaciaList.push(materialDetail?.gestor);
    }
    if (organizaciaList && organizaciaList.length !== 0 && materialDetail?.spolugestori) {
      materialDetail?.spolugestori
        .filter(spolugestorOrg => !organizaciaList.some(org => spolugestorOrg.uuid === org.uuid))
        .forEach(spolugestorOrg => organizaciaList.push(spolugestorOrg));
    }
    if (organizaciaList !== null) {
      setGestor(materialDetail?.gestor ? materialDetail.gestor.uuid : null);
      setSpolugestori(materialDetail?.spolugestori.length !== 0
        ? materialDetail.spolugestori : [null]);
      organizaciaList.sort((a, b) => Intl.Collator()
        .compare(a.nazov, b.nazov));
    }
  }, [materialDetail.gestor, materialDetail.spolugestori, organizaciaList]);

  const {
    wrapClient: changeGestorClient,
    status: changeGestorStatus,
  } = useClient(changeGestor);

  const onChangeGestor = async (): Promise<void> => {
    const spolugestorList = spolugestori?.filter(spolugestor => spolugestor !== null)
      .map(selectedGestor => selectedGestor.uuid);
    await changeGestorClient(type, year, index, gestor, spolugestorList);
    navigate(routes.procesy.detail.withParams(type, year, index));
    displaySuccess('Vaša požiadavka bola úspešne vykonaná.');
  };

  const filteredOrganizacie = (): Array<Organizacia> => organizaciaList?.filter(org => org?.uuid !== gestor);

  const checkForSpolugestorInGestor = (targetGestorUuid: string): void => {
    const spolugestorWithoutGestor = spolugestori?.filter(spolugestor => spolugestor?.uuid !== targetGestorUuid);
    if (spolugestorWithoutGestor?.length === 0) {
      setSpolugestori([null]);
    } else {
      setSpolugestori(spolugestorWithoutGestor);
    }
  };

  return (
    <>
      <FormGroup
        className="idsk-!-width-one-half gestor-determine-input"
        error={!!getFieldErrorMessage('gestor')}
      >
        <Label htmlFor="gestor">Gestor
        </Label>
        <Row>
          <Col width="three-quarters" className="gestor-determine-column">
            <Loader loading={organizaciaListStatus.loading}>
              <Select
                id="gestor"
                value={gestor || ''}
                onChange={({ target }) => {
                  setGestor(target.value);
                  checkForSpolugestorInGestor(target.value);
                  clearFieldErrorMessage('gestor');
                }}
                error={getFieldErrorMessage('gestor')}
                name="gestor"
                width="full"
              >
                <option
                  key="emptyKey"
                  value=""
                  disabled
                  hidden
                >
                  Vyberte organizáciu
                </option>
                {(organizaciaList && organizaciaList?.length > 0) ? (
                  <>
                    {organizaciaList?.map(gestorData => (
                      <option
                        key={gestorData.uuid}
                        value={gestorData.uuid}
                      >{gestorData.nazov}
                      </option>
                    ))}
                  </>
                ) : (
                  <option key="emptyOption" value="emptyOption" disabled>
                    Nenašiel sa žiadny záznam.
                  </option>
                )}
              </Select>
            </Loader>
          </Col>
        </Row>
      </FormGroup>
      <OrganizaciaInput
        data={filteredOrganizacie()}
        itemList={spolugestori}
        setItemList={(spolugestoriData) => {
          setSpolugestori(spolugestoriData);
          clearFieldErrorMessage('spolugestori');
        }}
        addButtonLabel="+ Pridať ďalšieho spolugestora"
        label="Spolugestori"
        error={getFieldErrorMessage('spolugestori')}
        loading={organizaciaListStatus.loading}
        width="one-half"
        required={false}
      />
      <UrcenieGestoraButtons
        onClick={onChangeGestor}
        loading={changeGestorStatus.loading}
        isAdmin={isAdmin}
      />
    </>
  );
};

export default UrcenieGestora;
